import { type FC, useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import {
	VIDEO_BUDGET_EXCEEDED_CODE,
	VIDEO_GENERATION_MAX_ATTACHMENTS,
	VIDEO_OFFER_MAX_CHARS,
	VIDEO_PLATFORM_RULES,
	VIDEO_PROMPT_MAX_CHARS,
	type VideoGenerationDto,
	type VideoOrientation,
	type VideoPresenterOption,
	type VideoUsageDto,
} from "shared";
import { AlertType, useAlertEmit } from "../common/alert/Alert";
import { parseError, parseErrorCode } from "../common/error";
import { Modal, useModal } from "../common/modal/Modal";
import { ExtraForm } from "../common/pure/ExtraFormGroup";
import { Spinner } from "../common/Spinner";
import { Button } from "../components/Button";
import { ModalSelectMedia } from "../post/ModalSelectMedia";
import {
	formatMinutes,
	useVideoActions,
	useVideoOptions,
	useVideoUsage,
} from "./useVideoGenerations";

const wouldExceedBudget = (
	usage: VideoUsageDto | undefined,
	seconds: number,
) => {
	if (!usage || usage.budget.mode === "off" || usage.budget.minutes == null) {
		return false;
	}
	return usage.budgetMinutesUsed + seconds / 60 > usage.budget.minutes;
};

const PLATFORM_LABELS: Record<string, string> = {
	instagram: "Instagram",
	tiktok: "TikTok",
	youtube: "YouTube Shorts",
	facebook: "Facebook",
	threads: "Threads",
	linkedin: "LinkedIn",
	x: "X",
};

interface Props {
	/** Make a video for this post; omit for a standalone video. */
	postId?: number;
	onStarted?: (video: VideoGenerationDto) => void;
}

function PresenterSelect(props: {
	id: string;
	label: string;
	value: string;
	options: VideoPresenterOption[];
	onChange: (value: string) => void;
	ownedGroupLabel?: string;
}) {
	const owned = props.options.filter((o) => o.owned);
	const stock = props.options.filter((o) => !o.owned);
	const labelOf = (o: VideoPresenterOption) =>
		o.detail ? `${o.name} · ${o.detail}` : o.name;
	return (
		<Form.Group className="mb-2">
			<Form.Label htmlFor={props.id}>{props.label}</Form.Label>
			<ExtraForm.Select
				id={props.id}
				value={props.value}
				onChange={(e) => props.onChange(e.target.value)}
			>
				<option value="">Let Moxie pick</option>
				{owned.length > 0 && (
					<optgroup label={props.ownedGroupLabel ?? "From your HeyGen"}>
						{owned.map((o) => (
							<option key={o.id} value={o.id}>
								{labelOf(o)}
							</option>
						))}
					</optgroup>
				)}
				{stock.length > 0 && (
					<optgroup label={owned.length ? "Stock presenters" : "Presenters"}>
						{stock.map((o) => (
							<option key={o.id} value={o.id}>
								{labelOf(o)}
							</option>
						))}
					</optgroup>
				)}
			</ExtraForm.Select>
		</Form.Group>
	);
}

/** Story 5.2: length and soft budget next to Generate. HeyGen bills credits. */
function EstimateLine(props: { seconds: number; usage?: VideoUsageDto }) {
	const minutes = Math.round((props.seconds / 60) * 10) / 10;
	const budget = props.usage?.budget;
	const budgetLine =
		budget && budget.mode !== "off" && budget.minutes != null
			? ` · ${props.usage?.budgetMinutesUsed} of ${budget.minutes} min ${budget.period} budget`
			: "";
	return (
		<div className="text-muted small text-center mt-2">
			About {minutes} min of HeyGen credits{budgetLine}
		</div>
	);
}

function ScriptSection(props: { title: string; body: string }) {
	return (
		<div className="mb-3">
			<div className="fw-semibold mb-1">{props.title}</div>
			<div className="text-muted" style={{ whiteSpace: "pre-line" }}>
				{props.body}
			</div>
		</div>
	);
}

export const ModalMakeVideo: FC<Props> = ({ postId, onStarted }) => {
	const { close, open } = useModal();
	const emit = useAlertEmit();
	const { preview, start, saveBudget } = useVideoActions();
	const [topic, setTopic] = useState("");
	const [platform, setPlatform] = useState("");
	const [draft, setDraft] = useState<VideoGenerationDto | null>(null);
	const [advanced, setAdvanced] = useState(false);
	const [prompt, setPrompt] = useState("");
	const [orientation, setOrientation] = useState<VideoOrientation>("portrait");
	const [attachments, setAttachments] = useState<number[]>([]);
	const [avatarId, setAvatarId] = useState("");
	const [voiceId, setVoiceId] = useState("");
	const [styleId, setStyleId] = useState("");
	const [saveAsDefaults, setSaveAsDefaults] = useState(false);
	const [offer, setOffer] = useState("");
	const [showOffer, setShowOffer] = useState(false);
	const [budgetGate, setBudgetGate] = useState(false);
	const options = useVideoOptions(draft !== null);
	const usage = useVideoUsage(draft !== null);
	const autoStarted = useRef(false);

	const writeScript = () =>
		preview.mutate(
			{
				...(postId
					? { postId }
					: { topic: topic.trim(), platform: platform || undefined }),
				offer: offer.trim() || undefined,
			},
			{
				onSuccess: (dto) => {
					setDraft(dto);
					setPrompt(dto.prompt);
					setOrientation(dto.orientation);
					setAvatarId(dto.avatarId ?? "");
					setVoiceId(dto.voiceId ?? "");
					setStyleId(dto.styleId ?? "");
				},
			},
		);

	// biome-ignore lint/correctness/useExhaustiveDependencies: write the script once when opened from a post
	useEffect(() => {
		if (postId && !autoStarted.current) {
			autoStarted.current = true;
			writeScript();
		}
	}, [postId]);

	const pickAttachments = () =>
		open(ModalSelectMedia, {
			values: attachments,
			multiple: true,
			mediaHint: `Pick up to ${VIDEO_GENERATION_MAX_ATTACHMENTS} photos or MP4 clips of your real business. They're used as the main visuals.`,
			onChange: (ids: number[]) => {
				if (ids.length > VIDEO_GENERATION_MAX_ATTACHMENTS) {
					emit({
						type: AlertType.WARNING,
						content: `Only the first ${VIDEO_GENERATION_MAX_ATTACHMENTS} were kept.`,
					});
				}
				setAttachments(ids.slice(0, VIDEO_GENERATION_MAX_ATTACHMENTS));
			},
		});

	const runGenerate = (ignoreBudget: boolean) => {
		if (!draft) {
			return;
		}
		start.mutate(
			{
				id: draft.id,
				dto: {
					prompt: advanced ? prompt : undefined,
					orientation,
					attachments,
					avatarId: avatarId || null,
					voiceId: voiceId || null,
					styleId: styleId || null,
					saveAsDefaults,
					ignoreBudget: ignoreBudget || undefined,
				},
			},
			{
				onSuccess: (video) => {
					setBudgetGate(false);
					emit({
						type: AlertType.SUCCESS,
						content: postId
							? "Your video is being made. We'll add it to this post when it's ready."
							: "Your video is being made. It will appear in your media library when it's ready.",
					});
					onStarted?.(video);
					close();
				},
				onError: (error) => {
					if (parseErrorCode(error) === VIDEO_BUDGET_EXCEEDED_CODE) {
						setBudgetGate(true);
					}
				},
			},
		);
	};

	const generate = () => {
		if (!draft) {
			return;
		}
		if (
			!budgetGate &&
			wouldExceedBudget(usage.data, draft.targetSeconds) &&
			usage.data?.budget.mode !== "off"
		) {
			setBudgetGate(true);
			return;
		}
		runGenerate(false);
	};

	const raiseBudgetAndGenerate = () => {
		if (!draft || !usage.data?.budget) {
			return;
		}
		const needed = Math.ceil(
			usage.data.budgetMinutesUsed + draft.targetSeconds / 60 + 5,
		);
		const next = Math.max(needed, (usage.data.budget.minutes ?? 0) + 30);
		saveBudget.mutate(
			{
				mode:
					usage.data.budget.mode === "off" ? "warn" : usage.data.budget.mode,
				period: usage.data.budget.period,
				minutes: next,
			},
			{
				onSuccess: () => runGenerate(true),
				onError: (error) =>
					emit({
						type: AlertType.ERROR,
						content: parseError(error) ?? "Couldn't raise your budget.",
					}),
			},
		);
	};

	const error =
		budgetGate && parseErrorCode(start.error) === VIDEO_BUDGET_EXCEEDED_CODE
			? null
			: (preview.error ?? start.error);
	const promptTooLong = prompt.length > VIDEO_PROMPT_MAX_CHARS;
	const attachmentsSection = [
		attachments.length
			? `${attachments.length} of your photo${attachments.length === 1 ? "" : "s"} or clips used as the main visuals`
			: "No photos or clips attached — the presenter and stock visuals carry the video",
		...(draft?.sections.attachments?.split("\n").slice(1) ?? []),
	].join("\n");
	const offerField = (
		<Form.Group className="mb-3">
			<Form.Label htmlFor="video-offer">Current offer (optional)</Form.Label>
			<ExtraForm.Control
				id="video-offer"
				value={offer}
				maxLength={VIDEO_OFFER_MAX_CHARS}
				onChange={(e) => setOffer(e.target.value)}
				placeholder="e.g. $49 spring tune-up through May 31"
			/>
			<Form.Text className="text-muted">
				The video only mentions an offer if you type it here, word for word.
			</Form.Text>
		</Form.Group>
	);

	return (
		<Modal size="lg">
			<h4 className="mb-1">Make a video</h4>
			<p className="text-muted mb-3">
				Moxie writes a short script from your business details. Review it, then
				we'll turn it into a captioned video.
			</p>

			{error && (
				<div className="alert alert-danger py-2" role="alert">
					{parseError(error) ?? "Something went wrong. Try again."}
				</div>
			)}

			{!draft && !postId && (
				<>
					<Form.Group className="mb-2">
						<Form.Label htmlFor="video-topic">
							What should the video be about?
						</Form.Label>
						<ExtraForm.Control
							id="video-topic"
							value={topic}
							maxLength={500}
							onChange={(e) => setTopic(e.target.value)}
							placeholder="e.g. Our spring AC tune-up special"
						/>
					</Form.Group>
					<Form.Group className="mb-3">
						<Form.Label htmlFor="video-platform">
							Where will you post it?
						</Form.Label>
						<ExtraForm.Select
							id="video-platform"
							value={platform}
							onChange={(e) => setPlatform(e.target.value)}
						>
							<option value="">Anywhere</option>
							{Object.keys(VIDEO_PLATFORM_RULES).map((p) => (
								<option key={p} value={p}>
									{PLATFORM_LABELS[p] ?? p}
								</option>
							))}
						</ExtraForm.Select>
					</Form.Group>
					{offerField}
					<Button
						full
						onClick={writeScript}
						loading={preview.isPending}
						disabled={!topic.trim()}
					>
						Write the script
					</Button>
				</>
			)}

			{!draft && postId && (
				<div className="d-flex flex-column align-items-center gap-2 py-4">
					{preview.isPending && <Spinner />}
					<span className="text-muted">
						{preview.isPending
							? "Writing a script from your post…"
							: "Couldn't write the script."}
					</span>
					{preview.isError && (
						<Button variant="secondary" onClick={writeScript}>
							Try again
						</Button>
					)}
				</div>
			)}

			{draft && (
				<>
					{advanced ? (
						<Form.Group className="mb-3">
							<Form.Label htmlFor="video-prompt">Script</Form.Label>
							<Form.Control
								as="textarea"
								id="video-prompt"
								rows={12}
								value={prompt}
								onChange={(e) => setPrompt(e.target.value)}
								isInvalid={promptTooLong || !prompt.trim()}
							/>
							<Form.Text
								className={promptTooLong ? "text-danger" : "text-muted"}
							>
								{prompt.length.toLocaleString()} /{" "}
								{VIDEO_PROMPT_MAX_CHARS.toLocaleString()} characters
							</Form.Text>
						</Form.Group>
					) : (
						<div className="p-3 border rounded mb-3">
							<ScriptSection
								title="What it says"
								body={draft.sections.whatItSays}
							/>
							<ScriptSection
								title="How it looks"
								body={draft.sections.howItLooks}
							/>
							<ScriptSection
								title="Length and format"
								body={draft.sections.lengthAndFormat}
							/>
							<ScriptSection title="Attachments" body={attachmentsSection} />
						</div>
					)}
					<div className="d-flex flex-wrap gap-3 mb-3">
						<Form.Check
							type="switch"
							id="video-advanced"
							label="Edit the full script (advanced)"
							checked={advanced}
							onChange={() => setAdvanced((v) => !v)}
						/>
						{!showOffer && (
							<Button
								variant="link"
								size="sm"
								className="p-0"
								onClick={() => setShowOffer(true)}
							>
								{offer.trim() ? "Change the offer" : "Add a current offer"}
							</Button>
						)}
					</div>
					{showOffer && (
						<div className="p-3 border rounded mb-3">
							{offerField}
							<Button
								variant="secondary"
								size="sm"
								loading={preview.isPending}
								onClick={() => {
									setShowOffer(false);
									setAdvanced(false);
									writeScript();
								}}
							>
								Rewrite the script
							</Button>
						</div>
					)}

					<Form.Group className="mb-3">
						<Form.Label htmlFor="video-orientation">Shape</Form.Label>
						<ExtraForm.Select
							id="video-orientation"
							value={orientation}
							onChange={(e) =>
								setOrientation(e.target.value as VideoOrientation)
							}
						>
							<option value="portrait">
								Portrait (9:16) · Reels, Stories, Shorts
							</option>
							<option value="landscape">
								Landscape (16:9) · LinkedIn, X, YouTube
							</option>
						</ExtraForm.Select>
					</Form.Group>

					<div className="mb-3">
						<div className="fw-semibold mb-1">Your photos and clips</div>
						<div className="d-flex align-items-center gap-2">
							<Button
								variant="secondary"
								size="sm"
								icon="add_photo_alternate"
								onClick={pickAttachments}
							>
								{attachments.length ? "Change" : "Add photos or clips"}
							</Button>
							<span className="text-muted">
								{attachments.length
									? `${attachments.length} attached`
									: "Optional. Real photos make the video feel like your business."}
							</span>
							{attachments.length > 0 && (
								<Button
									variant="link"
									size="sm"
									onClick={() => setAttachments([])}
								>
									Remove
								</Button>
							)}
						</div>
					</div>

					{options.isLoading && <Spinner />}
					{options.data && (
						<>
							<PresenterSelect
								id="video-avatar"
								label="Presenter"
								value={avatarId}
								options={options.data.avatars}
								ownedGroupLabel="Your HeyGen avatars"
								onChange={setAvatarId}
							/>
							<PresenterSelect
								id="video-voice"
								label="Voice"
								value={voiceId}
								options={options.data.voices}
								ownedGroupLabel="Your HeyGen voices"
								onChange={setVoiceId}
							/>
							<PresenterSelect
								id="video-style"
								label="Style"
								value={styleId}
								options={options.data.styles}
								onChange={setStyleId}
							/>
							<Form.Check
								id="video-save-defaults"
								className="mb-3"
								label="Use these for my next videos"
								checked={saveAsDefaults}
								onChange={() => setSaveAsDefaults((v) => !v)}
							/>
						</>
					)}
					{options.isError && (
						<p className="text-muted">
							Presenter choices couldn't load, so Moxie will pick for you.
						</p>
					)}

					<p className="text-muted mb-3">
						About {draft.targetSeconds} seconds long. Usually ready in about{" "}
						{formatMinutes(draft.estimatedRenderSeconds)}. HeyGen bills your
						HeyGen credits when the video finishes.
					</p>

					{budgetGate ? (
						<div className="alert alert-warning py-2" role="alert">
							<p className="mb-2">
								This video would go past your{" "}
								{usage.data?.budget.period ?? "monthly"} budget (
								{usage.data?.budgetMinutesUsed ?? "?"} of{" "}
								{usage.data?.budget.minutes ?? "?"} min used). HeyGen still
								bills your credits if you continue.
							</p>
							<div className="d-flex flex-column gap-2">
								{usage.data?.budget.mode !== "block" && (
									<Button
										full
										loading={start.isPending}
										onClick={() => runGenerate(true)}
									>
										Continue anyway
									</Button>
								)}
								<Button
									full
									variant="secondary"
									loading={saveBudget.isPending || start.isPending}
									onClick={raiseBudgetAndGenerate}
								>
									Raise limit and continue
								</Button>
								<Button
									full
									variant="secondary"
									onClick={() => setBudgetGate(false)}
								>
									Back
								</Button>
							</div>
						</div>
					) : (
						<>
							<div className="d-flex gap-2">
								<Button full variant="secondary" onClick={() => close()}>
									Cancel
								</Button>
								<Button
									full
									icon="movie"
									onClick={generate}
									loading={start.isPending}
									disabled={advanced && (promptTooLong || !prompt.trim())}
								>
									Make the video
								</Button>
							</div>
							<EstimateLine seconds={draft.targetSeconds} usage={usage.data} />
						</>
					)}
				</>
			)}
		</Modal>
	);
};
